import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Container, Alert, CircularProgress, Button, Stack,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveBar from './SaveBar';
import RecomputeLeaderboardDialog, { ChangedGame, GameWarnings } from './RecomputeLeaderboardDialog';
import { editorApi, ValidationError } from '../api/client';
import { UseEditorSession } from '../hooks/useEditorSession';

interface Props<T> {
  title: string;
  session: UseEditorSession<T>;
  children: (content: T) => React.ReactNode;
  // Override the top-bar back button. When provided, the default
  // (confirm + discard + navigate to /edit) is bypassed entirely —
  // useful for in-editor navigation (e.g. detail → list) that should
  // preserve the open session.
  onBack?: () => void | Promise<void>;
  // When set, after a successful commit the user is prompted to also
  // recompute the leaderboard. Use for files whose changes affect the
  // leaderboard (palio_games_status, palio).
  promptRecomputeLeaderboard?: boolean;
}

export function EditorShell<T>({
  title, session, children, onBack, promptRecomputeLeaderboard,
}: Props<T>) {
  const navigate = useNavigate();
  const {
    loading, content, error, externallyChanged, dirty, saving, committing,
    save, saveAndCommit, discard, reopen,
  } = session;
  const [justSaved, setJustSaved] = useState(false);

  // After a successful commit the session is closed server-side; reopen a
  // fresh one in place so the user keeps editing where they were instead
  // of being bounced back to the /edit menu.
  const finishCommit = async () => {
    await reopen();
    setJustSaved(true);
  };

  const handleBack = async () => {
    if (onBack) {
      await onBack();
      return;
    }
    if (dirty && !window.confirm('Modifiche non salvate. Uscire comunque?')) return;
    await discard();
    navigate('/edit', { replace: true });
  };

  const [recompute, setRecompute] = useState<{
    proposed: any;
    changedGames: ChangedGame[];
    skippedManual: ChangedGame[];
    warnings: GameWarnings[];
  } | null>(null);
  // Set when the commit succeeded but the recompute preview refused to
  // rank (e.g. non-numeric scores) — the edits are saved, the classifica
  // was left untouched, and the user should know why.
  const [recomputeWarning, setRecomputeWarning] = useState<string | null>(null);

  const handleCommit = async () => {
    setRecomputeWarning(null);
    try {
      if (!promptRecomputeLeaderboard) {
        await saveAndCommit();
        await finishCommit();
        return;
      }
      // Persist pending edits via write-through so the preview sees
      // them, then run the recompute preview BEFORE finalising the
      // commit. If the leaderboard would change, prompt the user and
      // bundle the recompute into the same save commit.
      await save();
      let preview;
      try {
        preview = await editorApi.previewLeaderboard();
      } catch (e) {
        if (e instanceof ValidationError) setRecomputeWarning(e.detail);
        await saveAndCommit();
        await finishCommit();
        return;
      }
      // Non-rankable games (non-numeric scores) aren't an error: their
      // entry is left alone and the user is asked to rank them by hand.
      const nonNumeric = preview.skipped_non_numeric ?? [];
      if (nonNumeric.length) {
        setRecomputeWarning(
          `Per ${nonNumeric.map((g) => g.name).join(', ')} i punteggi non `
          + 'sono numerici: inserisci l\'ordine di arrivo a mano nella '
          + 'sezione Classifica.',
        );
      }
      const warnings = preview.warnings ?? [];
      if (!preview.changed_games?.length) {
        // Nothing to recompute — still surface scoring warnings (missing
        // tie-break data, unmarked finals) in the standalone alert.
        if (warnings.length) {
          setRecomputeWarning(
            warnings
              .flatMap((g) => g.messages.map((m) => `${g.name}: ${m}`))
              .join(' — '),
          );
        }
        await saveAndCommit();
        await finishCommit();
        return;
      }
      setRecompute({
        proposed: preview.proposed,
        changedGames: preview.changed_games,
        skippedManual: preview.skipped_manual ?? [],
        warnings,
      });
    } catch {
      // error / externallyChanged already in session state
    }
  };

  const handleRecomputeApply = async () => {
    if (!recompute) return;
    setRecompute(null);
    try {
      await saveAndCommit(recompute.proposed);
      await finishCommit();
    } catch {
      // error / externallyChanged already in session state
    }
  };

  const handleRecomputeSkip = async () => {
    setRecompute(null);
    try {
      await saveAndCommit();
      await finishCommit();
    } catch {
      // error / externallyChanged already in session state
    }
  };

  const handleDiscard = async () => {
    if (dirty && !window.confirm('Annullare tutte le modifiche?')) return;
    await discard();
    navigate('/edit', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={handleBack} aria-label="indietro">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>{title}</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ flex: 1, py: 2, pb: 10 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {externallyChanged && (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <Alert severity="warning" sx={{ width: '100%' }}>
              {externallyChanged}
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => navigate('/edit', { replace: true })} variant="outlined">
                Torna indietro
              </Button>
              <Button startIcon={<RefreshIcon />} onClick={reopen} variant="contained">
                Ricarica ultimo stato
              </Button>
            </Stack>
          </Stack>
        )}

        {error && !externallyChanged && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {recomputeWarning && !externallyChanged && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setRecomputeWarning(null)}
          >
            {recomputeWarning}
          </Alert>
        )}

        {!loading && !externallyChanged && content != null && children(content)}
      </Container>

      {!loading && !externallyChanged && content != null && (
        <SaveBar
          dirty={dirty}
          saving={saving}
          committing={committing}
          onCommit={handleCommit}
          onDiscard={handleDiscard}
        />
      )}

      <RecomputeLeaderboardDialog
        open={recompute != null}
        onApply={handleRecomputeApply}
        onSkip={handleRecomputeSkip}
        changedGames={recompute?.changedGames ?? []}
        skippedManual={recompute?.skippedManual ?? []}
        warnings={recompute?.warnings ?? []}
      />

      <Snackbar
        open={justSaved}
        autoHideDuration={2500}
        onClose={() => setJustSaved(false)}
        message="Salvato ✓"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: 8 }}
      />
    </Box>
  );
}
